import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { 
  ArrowLeft, 
  Loader2,
  RefreshCw,
  AlertTriangle,
  CalendarIcon,
  Brain,
  Gauge,
  Settings
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import QuestionCard from "@/components/quiz/QuestionCard";
import CurrentAffairQuizResult from "./CurrentAffairQuizResult";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useCurrentAffairsAdaptiveQuiz } from "@/hooks/useCurrentAffairsAdaptiveQuiz";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

const CurrentAffairsAdaptiveQuizContent: React.FC = () => {
  const [view, setView] = useState<"intro" | "quiz" | "results">("intro");
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<string | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<'english' | 'hindi' | 'hinglish'>('english');
  const [initialDifficulty, setInitialDifficulty] = useState<"easy" | "medium" | "hard">("medium");
  const navigate = useNavigate();
  
  const {
    state,
    answerQuestion,
    nextQuestion,
    toggleTranslation,
    getResults,
    resetQuiz,
    setDateFilter,
    adaptiveDifficulty,
    saveQuizResults
  } = useCurrentAffairsAdaptiveQuiz(initialDifficulty);

  const handleDateChange = (date: Date | undefined) => {
    setSelectedDate(date);
    
    if (date) {
      toast.info(`Selected date: ${format(date, "PPP")}`, {
        description: "Quiz will show questions for this specific date"
      });
      setDateFilter(date, selectedTimePeriod);
    } else {
      toast.info("Date selection cleared", {
        description: "Quiz will show latest Current Affairs questions"
      });
      setDateFilter(null);
    }
  };

  const handleLanguageChange = (value: 'english' | 'hindi' | 'hinglish') => {
    setSelectedLanguage(value);
    toast.info(`Language changed to ${value}`);
  };

  const handleDifficultyChange = (value: "easy" | "medium" | "hard") => {
    setInitialDifficulty(value);
    toast.info(`Initial difficulty set to ${value}`);
  };

  const handleStartQuiz = () => {
    if (state.questions.length === 0) {
      toast.error("No questions available. Please try a different date or difficulty level.");
      return;
    }
    
    resetQuiz();
    setView("quiz");
    toast.success(`Starting adaptive quiz with ${state.questions.length} questions`);
  };

  const handleAnswer = (selected: number) => {
    answerQuestion(selected);
    setTimeout(() => {
      nextQuestion();
    }, 1000);
  };

  const handleBackToIntro = () => {
    setView("intro");
  };

  const handleFinishQuiz = () => {
    // Save quiz results to the database before showing results
    saveQuizResults().then((success) => {
      if (success) {
        toast.success("Quiz results saved to history");
      }
      setView("results");
    });
  };

  const handleNextOrFinish = () => {
    if (state.currentQuestionIndex >= state.questions.length - 1) {
      handleFinishQuiz();
    } else {
      nextQuestion();
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "easy": return "bg-green-500";
      case "medium": return "bg-blue-500";
      case "hard": return "bg-red-500";
      default: return "bg-gray-500";
    }
  };

  useEffect(() => {
    if (state.currentQuestionIndex >= state.questions.length && state.questions.length > 0) {
      handleFinishQuiz();
    }
  }, [state.currentQuestionIndex, state.questions.length]);

  if (view === "intro") {
    return (
      <div className="p-4 max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-4">
          <Button
            variant="ghost"
            onClick={() => navigate("/current-affairs")}
            size="sm"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Current Affairs
          </Button>
        </div>

        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <h1 className="text-2xl font-bold">Adaptive Current Affairs Quiz</h1>
            <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-300">
              <Brain className="h-3 w-3 mr-1" />
              Adaptive
            </Badge>
          </div>
          <p className="text-muted-foreground mb-6">
            The quiz difficulty adjusts in real-time based on your performance
          </p>
        </div>

        {state.error && (
          <div className="mb-6 p-4 border border-amber-200 bg-amber-50 rounded-lg text-amber-800 flex items-start">
            <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
            <p>{state.error}</p>
          </div>
        )}

        <div className="grid gap-6">
          <div className="bg-card border rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-4 mb-4">
              <Gauge className="h-8 w-8 text-purple-500" />
              <div>
                <h2 className="text-xl font-semibold">Start Adaptive Quiz</h2>
                <p className="text-muted-foreground text-sm">
                  The difficulty adjusts based on your performance
                </p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex flex-col space-y-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Starting Difficulty:</span>
                  <Select
                    value={initialDifficulty}
                    onValueChange={(value: "easy" | "medium" | "hard") => handleDifficultyChange(value)}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Select difficulty" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Easy</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="hard">Hard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Language:</span>
                  <Select
                    value={selectedLanguage}
                    onValueChange={(value: 'english' | 'hindi' | 'hinglish') => 
                      handleLanguageChange(value)
                    }
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="english">English</SelectItem>
                      <SelectItem value="hindi">हिंदी</SelectItem>
                      <SelectItem value="hinglish">Hinglish</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Date:</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-[240px] justify-start text-left font-normal",
                          !selectedDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedDate ? format(selectedDate, "PPP") : <span>Show all dates</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <div className="p-2 flex justify-between items-center border-b">
                        <span className="text-sm font-medium">Filter by date</span>
                        {selectedDate && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDateChange(undefined)}
                            className="h-8 px-2 text-xs"
                          >
                            Clear
                          </Button>
                        )}
                      </div>
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={handleDateChange}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-2">
                <Button 
                  className="flex-1" 
                  size="lg"
                  onClick={handleStartQuiz}
                  disabled={state.isLoading}
                >
                  {state.isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading Questions...
                    </>
                  ) : (
                    <>
                      <Brain className="mr-2 h-4 w-4" />
                      Start Adaptive Quiz
                    </>
                  )}
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => setDateFilter(selectedDate, selectedTimePeriod)}
                  disabled={state.isLoading}
                  className="flex-initial"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${state.isLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>
          </div>

          <div className="bg-card border rounded-lg p-6 shadow-sm">
            <h3 className="font-semibold mb-4">How Adaptive Quizzing Works</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <div className="mt-1 h-4 w-4 rounded-full bg-green-500 flex-shrink-0"></div>
                <p><span className="font-medium">Easy questions</span>: If you struggle with questions, the system will adjust to easier ones.</p>
              </div>
              <div className="flex items-start gap-2">
                <div className="mt-1 h-4 w-4 rounded-full bg-blue-500 flex-shrink-0"></div>
                <p><span className="font-medium">Medium questions</span>: The default difficulty level to start with.</p>
              </div>
              <div className="flex items-start gap-2">
                <div className="mt-1 h-4 w-4 rounded-full bg-red-500 flex-shrink-0"></div>
                <p><span className="font-medium">Hard questions</span>: As you succeed, the system gradually increases the difficulty.</p>
              </div>
              <p className="text-muted-foreground italic">
                The system adapts in real-time based on your performance, helping you learn more effectively.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (view === "quiz" && state.questions.length > 0) {
    const currentQuestion = state.questions[state.currentQuestionIndex];
    if (!currentQuestion) {
      return (
        <div className="flex items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }
    
    return (
      <div className="p-4 max-w-4xl mx-auto">
        <div className="mb-6 flex justify-between items-center">
          <Button
            variant="ghost"
            onClick={handleBackToIntro}
            size="sm"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          
          <div className="flex items-center gap-3">
            <Badge className={`${getDifficultyColor(state.difficulty)} text-white`}>
              <Gauge className="h-3 w-3 mr-1" />
              {state.difficulty.charAt(0).toUpperCase() + state.difficulty.slice(1)} Difficulty
            </Badge>
            
            <Select
              value={selectedLanguage}
              onValueChange={(value: 'english' | 'hindi' | 'hinglish') => 
                handleLanguageChange(value)
              }
            >
              <SelectTrigger className="w-[130px] h-9 text-xs">
                <Settings className="h-3 w-3 text-gray-500 mr-2" />
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="english">English</SelectItem>
                <SelectItem value="hindi">हिंदी</SelectItem>
                <SelectItem value="hinglish">Hinglish</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="mb-4">
          <div className="flex justify-between text-sm text-muted-foreground mb-1">
            <span>Progress</span>
            <span>{state.currentQuestionIndex + 1}/{state.questions.length}</span>
          </div>
          <Progress value={(state.currentQuestionIndex + 1) / state.questions.length * 100} className="h-2" />
        </div>
        
        <div className="mt-4">
          <QuestionCard
            question={{
              ...currentQuestion,
              options: currentQuestion.options,
              correctanswer: currentQuestion.correctAnswer,
              subject: "Current Affairs",
              category: "Current Affairs",
              question_type: 'mcq'
            }}
            questionNumber={state.currentQuestionIndex + 1}
            totalQuestions={state.questions.length}
            selectedAnswer={state.answers[state.currentQuestionIndex]}
            onSelect={handleAnswer}
            onNext={handleNextOrFinish}
            selectedLanguage={selectedLanguage}
            showBilingual={true}
          />
        </div>
      </div>
    );
  }

  if (view === "results") {
    const results = getResults();
    const timeSpent = results.timeSpent || { minutes: 0, seconds: 0 };
    
    return (
      <div className="p-4 max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={handleBackToIntro}
          size="sm"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Quiz
        </Button>
        
        <div className="mt-4">
          <CurrentAffairQuizResult
            results={results}
            timeSpent={timeSpent}
            questions={results.allQuestions || state.questions} 
            answers={results.allAnswers || state.answers} 
            difficulty={state.difficulty}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center p-8">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
};

export default CurrentAffairsAdaptiveQuizContent;
